/**
 * cdn-domain-error-pages-tab.tsx — Error Pages tab for a CDN domain (act-as).
 *
 * Mirrors Partner's ErrorPagesTab.tsx: master toggle, status code picker with
 * quick-add buttons, collapsible per-code HTML editor with iframe preview.
 *
 * Endpoints: GET/PUT/DELETE /api/v3/cdn/domains/:id/error-pages
 *   PUT sends { enabled, pages } — replaces all configured pages.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, ChevronDown, ChevronUp, FileX } from 'lucide-react';
import { Section, SettingRow, ActionBar } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  Skeleton,
  Badge,
  Switch,
  Textarea,
  Input,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { EmptyState } from '@vnetwork/ui/data';
import { TabError } from '@/pages/customers/detail/tab-states';
import {
  useActAsCDNErrorPages,
  actAsCDNRulesKeys,
} from '@/api/act-as-cdn-rules';

// API shape: pages is a Record<statusCode, html>.
// Locally we work with a sorted array for stable rendering.
interface LocalPage { code: number; html: string; }
import { actAsClient } from '@/api/act-as-client';
import { ActAsMutationConfirm } from '../act-as-mutation-confirm';

/* ── Quick-add code groups ───────────────────────────────────────────────── */

const CODES_4XX = [400, 401, 403, 404, 405, 406, 408, 410, 413, 415, 429, 451];
const CODES_5XX = [500, 502, 503, 504, 520, 521, 522, 523, 524];

const DEFAULT_HTML = (code: number) =>
  `<!DOCTYPE html>\n<html>\n<head><title>Error ${code}</title></head>\n<body>\n  <h1>Error ${code}</h1>\n  <p>Sorry, something went wrong.</p>\n</body>\n</html>`;

/* ── Per-code editor row ─────────────────────────────────────────────────── */

interface CodeEditorProps {
  page: LocalPage;
  onChange: (page: LocalPage) => void;
  onRemove: () => void;
}

function CodeEditor({ page, onChange, onRemove }: CodeEditorProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);

  // Replace {code} token for preview
  const previewHtml = page.html.replace(/\{code\}/g, String(page.code));

  return (
    <Card>
      <CardHeader
        actions={
          <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded((v) => !v)}
              aria-label={expanded ? t('common.collapse', 'Collapse') : t('common.expand', 'Expand')}
            >
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </Button>
            <Button variant="danger" size="sm" onClick={onRemove}>
              {t('common.remove', 'Remove')}
            </Button>
          </div>
        }
      >
        <CardTitle>
          <Badge
            tone={page.code >= 500 ? 'danger' : 'warning'}
            appearance="subtle"
          >
            {page.code}
          </Badge>
        </CardTitle>
      </CardHeader>
      {expanded && (
        <CardBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: '1fr 1fr' }}>
            <Field
              label={t('cdn_domain.error_pages.field_html', 'HTML content')}
              description={t(
                'cdn_domain.error_pages.field_html_desc',
                'Use {code} as a placeholder for the status code.',
              )}
            >
              {(props) => (
                <Textarea
                  {...props}
                  value={page.html}
                  rows={12}
                  style={{
                    fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)',
                    fontSize: 'var(--vn-font-size-sm)',
                  }}
                  onChange={(e) => onChange({ ...page, html: e.target.value })}
                />
              )}
            </Field>
            <div>
              <p
                style={{
                  margin: '0 0 var(--vn-space-2)',
                  fontSize: 'var(--vn-font-size-sm)',
                  color: 'var(--vn-fg-muted)',
                }}
              >
                {t('cdn_domain.error_pages.preview_label', 'Preview')}
              </p>
              <iframe
                title={`Preview ${page.code}`}
                srcDoc={previewHtml}
                style={{
                  width: '100%',
                  height: 260,
                  border: '1px solid var(--vn-border-subtle)',
                  borderRadius: 'var(--vn-radius-md)',
                  background: '#fff',
                }}
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        </CardBody>
      )}
    </Card>
  );
}

/* ── Tab component ───────────────────────────────────────────────────────── */

interface CdnDomainErrorPagesTabProps {
  domainId: string;
}

export function CdnDomainErrorPagesTab({ domainId }: CdnDomainErrorPagesTabProps) {
  const { t } = useTranslation();
  const q = useActAsCDNErrorPages(domainId);

  const [enabled, setEnabled] = useState(false);
  const [pages, setPages] = useState<LocalPage[]>([]);
  const [customCode, setCustomCode] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    if (q.data) {
      setEnabled(q.data.enabled ?? false);
      // Deserialize Record<string, string> → sorted LocalPage[]
      const loaded: LocalPage[] = Object.entries(q.data.pages ?? {})
        .map(([k, html]) => ({ code: parseInt(k, 10), html }))
        .sort((a, b) => a.code - b.code);
      setPages(loaded);
    }
  }, [q.data]);

  const usedCodes = new Set(pages.map((p) => p.code));

  const addCode = (code: number) => {
    if (usedCodes.has(code)) return;
    setPages((prev) => [...prev, { code, html: DEFAULT_HTML(code) }].sort((a, b) => a.code - b.code));
  };

  const addCustomCode = () => {
    const code = parseInt(customCode, 10);
    if (!code || code < 400 || code > 599) return;
    addCode(code);
    setCustomCode('');
  };

  const updatePage = (idx: number, page: LocalPage) => {
    setPages((prev) => prev.map((p, i) => (i === idx ? page : p)));
  };

  const removePage = (idx: number) => {
    setPages((prev) => prev.filter((_, i) => i !== idx));
  };

  // Serialize LocalPage[] → Record<string, string> for the API
  const pagesRecord = Object.fromEntries(pages.map((p) => [String(p.code), p.html]));
  const payload = { enabled, pages: pagesRecord };

  if (q.isLoading) return <Skeleton style={{ height: 240 }} />;
  if (q.isError) {
    return (
      <TabError
        title={t('cdn_domain.error_pages.load_error', 'Failed to load error pages')}
        onRetry={() => q.refetch()}
      />
    );
  }

  return (
    <Section
      title={t('cdn_domain.error_pages.title', 'Error Pages')}
      description={t(
        'cdn_domain.error_pages.description',
        'Serve custom HTML pages for specific HTTP error status codes.',
      )}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {/* ── Master toggle ── */}
        <Card>
          <CardBody>
            <SettingRow
              title={t('cdn_domain.error_pages.field_enabled', 'Custom error pages enabled')}
              description={t(
                'cdn_domain.error_pages.field_enabled_desc',
                'Serve the HTML below instead of provider default error pages.',
              )}
              control={
                <Switch checked={enabled} onCheckedChange={setEnabled} />
              }
            />
          </CardBody>
        </Card>

        {/* ── Quick-add codes ── */}
        <Card>
          <CardHeader>
            <CardTitle>{t('cdn_domain.error_pages.section_add', 'Add status code')}</CardTitle>
            <CardDescription>
              {t('cdn_domain.error_pages.section_add_desc', 'Click a code to add a custom page for it.')}
            </CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <div>
              <p style={{ margin: '0 0 var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                {t('cdn_domain.error_pages.group_4xx', '4xx — Client errors')}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
                {CODES_4XX.map((code) => (
                  <Badge
                    key={code}
                    tone={usedCodes.has(code) ? 'neutral' : 'warning'}
                    appearance="subtle"
                    size="sm"
                    style={{ cursor: usedCodes.has(code) ? 'default' : 'pointer', opacity: usedCodes.has(code) ? 0.4 : 1 }}
                    onClick={() => addCode(code)}
                  >
                    {code}
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <p style={{ margin: '0 0 var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                {t('cdn_domain.error_pages.group_5xx', '5xx — Server errors')}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
                {CODES_5XX.map((code) => (
                  <Badge
                    key={code}
                    tone={usedCodes.has(code) ? 'neutral' : 'danger'}
                    appearance="subtle"
                    size="sm"
                    style={{ cursor: usedCodes.has(code) ? 'default' : 'pointer', opacity: usedCodes.has(code) ? 0.4 : 1 }}
                    onClick={() => addCode(code)}
                  >
                    {code}
                  </Badge>
                ))}
              </div>
            </div>
            {/* Custom code input */}
            <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
              <Input
                value={customCode}
                placeholder={t('cdn_domain.error_pages.custom_code_placeholder', 'Custom code (e.g. 451)')}
                onChange={(e) => setCustomCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
                onKeyDown={(e) => e.key === 'Enter' && addCustomCode()}
                style={{ maxWidth: 200 }}
              />
              <Button
                variant="secondary"
                size="sm"
                startIcon={<Plus size={14} />}
                onClick={addCustomCode}
                disabled={!customCode || parseInt(customCode, 10) < 400}
              >
                {t('cdn_domain.error_pages.add_custom', 'Add')}
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* ── Configured pages ── */}
        {pages.length === 0 ? (
          <EmptyState
            icon={<FileX size={28} />}
            title={t('cdn_domain.error_pages.empty_title', 'No custom error pages yet')}
            description={t(
              'cdn_domain.error_pages.empty_desc',
              'Add a status code above to create a custom error page.',
            )}
          />
        ) : (
          pages.map((page, idx) => (
            <CodeEditor
              key={page.code}
              page={page}
              onChange={(updated) => updatePage(idx, updated)}
              onRemove={() => removePage(idx)}
            />
          ))
        )}

        <ActionBar
          start={
            q.data ? (
              <Button variant="danger" onClick={() => setDeleteConfirmOpen(true)}>
                {t('cdn_domain.error_pages.action_remove', 'Remove config')}
              </Button>
            ) : undefined
          }
          end={
            <Button onClick={() => setConfirmOpen(true)}>
              {t('cdn_domain.error_pages.action_save', 'Save')}
            </Button>
          }
        />
      </div>

      <ActAsMutationConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('cdn_domain.error_pages.confirm_save_title', 'Save error pages')}
        summary={t(
          'cdn_domain.error_pages.confirm_save_summary',
          '{{n}} custom error page(s) will be applied. Enabled: {{enabled}}.',
          { n: pages.length, enabled: enabled ? 'yes' : 'no' },
        )}
        queryKeysToInvalidate={[actAsCDNRulesKeys.errorPages(domainId)]}
        mutationFn={() => actAsClient.put(`/cdn/domains/${domainId}/error-pages`, payload)}
      />

      <ActAsMutationConfirm
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={t('cdn_domain.error_pages.confirm_delete_title', 'Remove error pages config')}
        summary={t(
          'cdn_domain.error_pages.confirm_delete_summary',
          'All custom error pages will be removed. Provider default error pages will be shown.',
        )}
        queryKeysToInvalidate={[actAsCDNRulesKeys.errorPages(domainId)]}
        mutationFn={() => actAsClient.delete(`/cdn/domains/${domainId}/error-pages`)}
        onSuccess={() => { setEnabled(false); setPages([] as LocalPage[]); }}
        destructive
        confirmLabel={t('cdn_domain.error_pages.action_remove', 'Remove config')}
      />
    </Section>
  );
}
